import React, { useEffect, useState } from 'react';
import Fields from 'components/Fields';
import { Chip, CircularProgress } from '@material-ui/core';
import { Button, Modal } from 'components/Base';
import api from 'api';
import BulkImport from 'components/BulkImport/BulkImport';
import { errHelper, Notify, unFormatPercent } from 'utils';
import Icons from 'components/Icons';
import { isEqual } from 'lodash';
import ModelPortfolioSelector from 'components/CipManagementv2/components/ModelPortfolioSelector';
import ManualFund from 'components/Forms/ExistingPolicies/ManagePolicies/Funds/ManualFund';
import { connect } from 'react-redux';
import { compose } from 'redux-starter-kit';
import actions from 'store/actions';
import SearchModelPortfolio from 'components/ModelPortfolio/Search';
import { makeStyles } from '@material-ui/styles';
import Store from 'store/store';
import CheckBoxOutlineBlankRoundedIcon from '@material-ui/icons/CheckBoxOutlineBlankRounded';
import CheckBoxRoundedIcon from '@material-ui/icons/CheckBoxRounded';
import CancelIcon from '@material-ui/icons/Cancel';
import AdvancedFilters from 'components/AdvancedFilters/AdvancedFilters';
import { fundActiveNotification } from 'components/Forms/ExistingPolicies/ManagePolicies/Funds/utils';

const { selectors } = Store;

const InvestmentFilter = ({
  updateAssetClasses,
  investments,
  updateResearchInvestments,
  selectedInvestment,
  modelPortfolios,
  advisor
}: any) => {
  const [activeAnalysis] = useState(selectedInvestment);
  const [funds, setFunds] = useState([] as any[]);
  const [mps, setMps] = useState([] as any[]);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [customMpEditInfo, setCustomMpEditInfo] = useState<{
    id?: number;
    mp_id?: number;
    last_updated?: string;
  }>({});
  const [modelPortfolioOption, setModelPortfolioOption] = useState('select');
  const [addingModelPortfolioSelection, setAddingModelPortfolioSelection] =
    useState(false);
  const [isModelPortfolioSelectorOpen, setIsModelPortfolioSelectorOpen] =
    useState(false);
  const [isManualFundModalOpen, setIsManualFundModalOpen] = useState({
    isVisible: false,
    initialValues: {}
  });
  const [, setCheckedFunds] = useState(Array(funds.length).fill(false));
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [advancedFilterType, setAdvancedFilterType] = useState(
    'fund' as 'fund' | 'portfolio'
  );

  const handleCheckboxChange = (id: string) => {
    const updatedInvestments = [...investments];
    const index = updatedInvestments.findIndex(
      investment => investment.id === id
    );

    const selectedCount = updatedInvestments.filter(
      investment => investment.is_checked
    ).length;

    let isChecked = !updatedInvestments[index].is_checked;

    const maxSelectionLimit = 11;
    if (selectedCount > maxSelectionLimit && !!isChecked) {
      isChecked = false;
      Notify.error('You can only select up to 12 options to analyse.');
    }

    updatedInvestments[index].is_checked = isChecked;

    const data = {
      type: updatedInvestments[index].mp_id ? 'mp' : 'f',
      research_item_id: Number(activeAnalysis),
      fund_id: updatedInvestments[index].id,
      is_checked: isChecked,
      model_portfolio_selection_id: updatedInvestments[index].id,
      action: 'toggle'
    };
    api
      .updateResearchRelations([data])
      .then(() => {
        updateResearchInvestments(updatedInvestments);
      })
      .catch(err => errHelper(err));
  };

  useEffect(() => {
    api.getAssetClasses().then(response => {
      updateAssetClasses(response);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFunds(investments.filter((investment: any) => !investment.mp_id));
    setMps(investments.filter((investment: any) => investment.mp_id));
    setCheckedFunds(
      investments.filter((investment: any) => investment.is_checked)
    );
  }, [investments]);

  type MpMeta = {
    isSaved: boolean;
    isSelected: boolean;
    name: string;
    value: string;
    mp_id: string;
    source: string;
  };

  const saveMpInvestments = (mpInvestmentsToSave: MpMeta[] | MpMeta) => {
    setAddingModelPortfolioSelection(true);
    const isMultiple = Array.isArray(mpInvestmentsToSave);
    const toSaveRequests = isMultiple
      ? (mpInvestmentsToSave as MpMeta[]).map(its => ({
          type: 'mp',
          research_item_id: Number(activeAnalysis),
          model_portfolio_selection_id: its.value,
          is_checked: true,
          name: its.name,
          loading: true,
          mp_id: its.mp_id || -1,
          source: its.source || 'ia'
        }))
      : [
          {
            type: 'mp',
            research_item_id: Number(activeAnalysis),
            model_portfolio_selection_id: (mpInvestmentsToSave as MpMeta).value,
            is_checked: true,
            name: (mpInvestmentsToSave as MpMeta).name,
            loading: true,
            mp_id: (mpInvestmentsToSave as MpMeta).mp_id || -1,
            source: (mpInvestmentsToSave as MpMeta).source || 'ia'
          }
        ];

    const currentInvestments = [...investments];

    const updatedInvestments = currentInvestments.map(investment => {
      const isMatching = toSaveRequests.some(
        req => req.name === investment.name
      );
      return isMatching ? { ...investment, loading: true } : investment;
    });

    const nonDuplicatedSaveRequests = toSaveRequests.filter(
      req => !currentInvestments.some(inv => inv.name === req.name)
    );

    updateResearchInvestments([
      ...updatedInvestments,
      ...nonDuplicatedSaveRequests
    ]);

    if (modelPortfolioOption === 'select') {
      setIsModelPortfolioSelectorOpen(false);
    }

    api
      .updateResearchRelations(toSaveRequests)
      .then(response => {
        const investmentData = currentInvestments;
        const index = currentInvestments.findIndex(
          (investment: any) =>
            investment.id ===
              (!isMultiple ? parseInt(mpInvestmentsToSave.value) : 0) &&
            !!investment.mp_id
        );
        const updatingIndividual = index >= 0;
        if (updatingIndividual) {
          investmentData.splice(index, 1, response.data[0]);
        } else {
          investmentData.push(...response.data);
        }
        updateResearchInvestments([...investmentData]);
        if (!updatingIndividual) {
          Notify.success(
            `You have sucessfully added ${
              isMultiple ? (mpInvestmentsToSave as MpMeta[]).length : '1'
            } model portfolios`
          );
        }
      })
      .catch(err => errHelper(err))
      .finally(() => {
        setAddingModelPortfolioSelection(false);
      });
  };

  const isFundAlreadyAdded = (fund: any) => {
    return investments.reduce((acc: boolean, mp: any) => {
      if (mp.id === Number(fund.id)) {
        acc = true;
      }
      return acc;
    }, false);
  };

  const saveFundInvestments = (fundInvestmentsToSave: any) => {
    let showAddedWarning = false;
    let showInactiveWarning = false;
    const addingFunds = advancedFilterType === 'fund';
    const toSaveRequests = fundInvestmentsToSave.map((its: any) => {
      const id = its?.fund?.id || its.id;
      const name = its?.fund?.name || its.name;
      const item = {
        type: addingFunds ? 'f' : 'mp',
        research_item_id: Number(activeAnalysis),
        is_checked: true,
        name,
        loading: true
      } as any;

      if (!addingFunds) {
        item.model_portfolio_selection_id = id;
        item.mp_id = its.mp_id || -1;
      } else {
        item.fund_id = id;
      }

      return item;
    });

    if (!toSaveRequests.length) {
      Notify.error('No funds to save');
      return;
    }

    const currentInvestments = [...investments];
    updateResearchInvestments([...currentInvestments, toSaveRequests]);

    api
      .updateResearchRelations(toSaveRequests)
      .then(response => {
        const investmentData = currentInvestments;
        const fundsToAdd = response.data.filter((its: any) => {
          if (!addingFunds) {
            return true;
          }

          const isAlreadyAdded = isFundAlreadyAdded(its);
          const isFundActive = !its.active;

          if (isFundActive) {
            showInactiveWarning = true;
          }

          if (isAlreadyAdded) {
            showAddedWarning = true;
          }

          return !isAlreadyAdded && !isFundActive;
        });

        if (fundsToAdd.length > 0) {
          updateResearchInvestments([...investmentData, ...fundsToAdd]);
        }

        if (showInactiveWarning) fundActiveNotification();

        if (showAddedWarning && fundsToAdd.length === 0) {
          Notify.warn('Some funds are already added.');
        }

        if (
          !showInactiveWarning &&
          !showAddedWarning &&
          fundsToAdd.length > 0
        ) {
          Notify.success(
            `You have successfully added ${fundsToAdd.length} ${addingFunds ? 'fund' : 'portfolio'}(s).`
          );
        }
      })
      .catch(err => errHelper(err));
  };

  const saveFundInvestment = (fund: any) => {
    const toSave = {
      type: 'f',
      research_item_id: Number(activeAnalysis),
      fund_id: fund.id || fund.value,
      is_checked: true,
      name: fund.name,
      loading: true
    };
    const isFundActive = fund.active;

    if (!isFundActive) return fundActiveNotification();

    if (isFundAlreadyAdded(fund)) {
      Notify.warn(`${fund.name} is already added to this investment option`);
      return;
    }
    const currentInvestments = [...investments];
    updateResearchInvestments([...currentInvestments, toSave]);
    api
      .updateResearchRelations([toSave])
      .then(response => {
        const investmentData = currentInvestments;
        updateResearchInvestments([...investmentData, response.data[0]]);
        Notify.success(`You have sucessfully added ${fund.name} funds`);
      })
      .catch(err => errHelper(err));
  };

  const deleteInvestment = (investment: any) => {
    const toDelete = {
      type: investment.mp_id ? 'mp' : 'f',
      id: investment.id,
      research_item_id: Number(activeAnalysis),
      is_checked: true
    };
    api
      .deleteResearchRelations(toDelete)
      .then(() => {
        const index = investments.findIndex((listedInvestment: any) =>
          isEqual(listedInvestment, investment)
        );
        const listedInvestments = [...investments];
        listedInvestments.splice(index, 1);
        updateResearchInvestments(listedInvestments);
        Notify.success(`You have sucessfully deleted ${investment.name}`);
      })
      .catch(err => {
        errHelper(err);
      });
  };

  const addNewManualFund = (fund: any) => {
    const { fund_value } = fund;

    const preparedFund = {
      ...fund,
      ocf: (Number(unFormatPercent(fund.ocf || 0)) / 100).toFixed(4),
      trading_costs: Number(unFormatPercent(fund.trading_costs) / 100).toFixed(
        4
      ),
      asset_classes:
        fund.asset_classes &&
        fund.asset_classes.map((assetClass: any) => ({
          asset_class: assetClass.asset_class,
          percentage_value: (
            Number(unFormatPercent(assetClass.percentage_value)) / 100
          ).toFixed(4)
        }))
    };

    return api
      .addNewFund({ fundData: preparedFund })
      .then((resp: any) => {
        saveFundInvestment({ ...resp.data, fund_value: fund_value });
        setIsManualFundModalOpen({ isVisible: false, initialValues: {} });
      })
      .catch((err: any) => {
        if (err.response.data.message) {
          Notify.error(err.response.data.message);
        }
      });
  };

  const openModelPortfolioModal = (type: string) => {
    setModelPortfolioOption(type);
    setIsModelPortfolioSelectorOpen(true);
  };

  const addCipSelection = (data: any) => {
    if (data.type === 'f') {
      saveFundInvestment(data);
      return;
    }
    saveMpInvestments({ ...data, source: 'cip' });
  };

  const updateModelPortfolioSelection = (data: any) => {
    saveMpInvestments({ ...data, isSaved: true });
  };

  const useStyles = makeStyles({
    investmentWrapper: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '15px',
      padding: '1rem 2rem'
    },
    investment: {
      borderRadius: '15px',
      color: '#FFF',
      padding: '10px 20px',
      display: 'flex',
      gap: '10px'
    },
    borderBottom: {
      borderBottom: '1px solid #eaeaea'
    },
    category: {
      minHeight: '100px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    },
    categoryTitle: {
      padding: '1rem 2rem 1rem 2rem',
      width: '100%',
      display: 'flex',
      alignItems: 'center'
    },
    chip: {
      padding: '1.2rem 0.5rem',
      fontWeight: 500
    },
    editIcon: {
      display: 'flex',
      '& svg': {
        pointerEvents: 'none'
      }
    }
  });

  const classes = useStyles();

  return (
    <>
      <div className="investmentButtonBar" style={{ padding: '1rem 2rem' }}>
        <h3>CIP Selection</h3>
        <div style={{ width: '550px', paddingLeft: '2rem' }}>
          {modelPortfolios.length > 0 ? (
            <SearchModelPortfolio
              data={modelPortfolios}
              onSelect={addCipSelection}
              isLoading={false}
              inputId="search-cip-mp"
            />
          ) : (
            <div>You have no investment options setup to select</div>
          )}
        </div>
      </div>
      <div className={`${classes.borderBottom} ${classes.category}`}>
        <div className={classes.categoryTitle}>
          <span className="flexWrap" style={{ fontWeight: 'bold' }}>
            <div style={{ fontSize: '1.17em' }}>Portfolios</div>
          </span>
          <div>
            <Button
              type="submit"
              onClick={() => {
                openModelPortfolioModal('select');
              }}
              style={{ marginLeft: '2rem' }}
            >
              Add Model Portfolio
            </Button>
            <Button
              type="submit"
              onClick={() => {
                openModelPortfolioModal('create');
              }}
              style={{ marginLeft: '2rem' }}
            >
              Create Custom Portfolio
            </Button>
            {advisor.organization.is_beta && (
              <Button
                variant="contained"
                color="secondary"
                type="submit"
                size="medium"
                onClick={() => {
                  setAdvancedFilterType('portfolio');
                  setIsAdvancedFiltersOpen(true);
                }}
                style={{ marginLeft: '2rem' }}
              >
                Portfolio Filter
              </Button>
            )}
          </div>
        </div>
        {mps.length > 0 && (
          <div className={classes.investmentWrapper}>
            {mps.map(modelPortfolio => (
              <Chip
                key={modelPortfolio.id}
                className={classes.chip}
                icon={
                  modelPortfolio.loading ? (
                    <CircularProgress size={12} />
                  ) : modelPortfolio.is_checked ? (
                    <CheckBoxRoundedIcon />
                  ) : (
                    <CheckBoxOutlineBlankRoundedIcon />
                  )
                }
                color={modelPortfolio.is_checked ? 'primary' : 'default'}
                deleteIcon={
                  <CancelIcon
                    style={{
                      display: modelPortfolio.loading ? 'none' : 'block'
                    }}
                  />
                }
                onDelete={() => deleteInvestment(modelPortfolio)}
                onClick={e => {
                  const className = (e.target as any).className;
                  if (
                    className &&
                    typeof className === 'string' &&
                    className.includes('makeStyles-editIcon-')
                  )
                    return;
                  handleCheckboxChange(modelPortfolio.id);
                }}
                label={
                  modelPortfolio.mp_id && !modelPortfolio.cip_category ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {modelPortfolio.name}
                      {!modelPortfolio.loading && (
                        <div
                          className={classes.editIcon}
                          onClick={e => {
                            e.stopPropagation();
                            setCustomMpEditInfo({
                              ...customMpEditInfo,
                              id: modelPortfolio.id,
                              mp_id: modelPortfolio.mp_id,
                              last_updated: modelPortfolio.last_updated
                            });
                            openModelPortfolioModal('create');
                          }}
                        >
                          <Icons.Edit />
                        </div>
                      )}
                    </div>
                  ) : (
                    modelPortfolio.name
                  )
                }
              />
            ))}
          </div>
        )}
      </div>
      <div className={classes.category}>
        <div className={classes.categoryTitle}>
          <span style={{ fontWeight: 'bold', fontSize: '1.17em' }}>Funds</span>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: '550px', paddingLeft: '2rem' }}>
              <Fields.Search onSelect={val => saveFundInvestment(val)} />
            </div>
            <Button
              type="submit"
              onClick={() => setIsBulkImportOpen(true)}
              style={{ marginLeft: '2rem' }}
            >
              Bulk Import
            </Button>
            <BulkImport
              openBulkImport={isBulkImportOpen}
              setOpenBulkImport={setIsBulkImportOpen}
              importToPolicy={(imports: any) => saveFundInvestments(imports)}
            />
            <div style={{ marginLeft: '2rem' }}>
              <Button
                type="button"
                onClick={() =>
                  setIsManualFundModalOpen({
                    ...isManualFundModalOpen,
                    isVisible: true
                  })
                }
              >
                Add fund manually
              </Button>
              {advisor.organization.is_beta && (
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  size="medium"
                  onClick={() => {
                    setAdvancedFilterType('fund');
                    setIsAdvancedFiltersOpen(true);
                  }}
                  style={{ marginLeft: '2rem' }}
                >
                  Fund Filter
                </Button>
              )}
            </div>
          </div>
        </div>
        {funds.length > 0 && (
          <div className={classes.investmentWrapper}>
            {funds.map(fund => (
              <Chip
                key={fund.id}
                className={classes.chip}
                icon={
                  fund.loading ? (
                    <CircularProgress size={12} />
                  ) : fund.is_checked ? (
                    <CheckBoxRoundedIcon />
                  ) : (
                    <CheckBoxOutlineBlankRoundedIcon />
                  )
                }
                color={fund.is_checked ? 'primary' : 'default'}
                deleteIcon={
                  <CancelIcon
                    style={{ display: fund.loading ? 'none' : 'block' }}
                  />
                }
                onDelete={() => deleteInvestment(fund)}
                onClick={() => handleCheckboxChange(fund.id)}
                label={fund.name}
              />
            ))}
          </div>
        )}
      </div>
      <div style={{ width: '100%', height: 'max-content' }}>
        <ModelPortfolioSelector
          modelPortfolioId={customMpEditInfo.id}
          date={customMpEditInfo.last_updated}
          isOpen={isModelPortfolioSelectorOpen}
          closeModelFunc={() => {
            setIsModelPortfolioSelectorOpen(false);
            setCustomMpEditInfo({});
          }}
          addModelPortfolioSelection={
            customMpEditInfo.mp_id
              ? updateModelPortfolioSelection
              : saveMpInvestments
          }
          onClone={() => setCustomMpEditInfo({})}
          addingModelPortfolioSelection={addingModelPortfolioSelection}
          investments={investments}
          option={modelPortfolioOption}
        />
      </div>
      <Modal
        closeModalFunc={() =>
          setIsManualFundModalOpen({ isVisible: false, initialValues: {} })
        }
        isOpen={isManualFundModalOpen.isVisible}
        disableBackdropClick
        disableEscapeKeyDown
      >
        <ManualFund
          onSubmit={addNewManualFund}
          closeModalFunc={() =>
            setIsManualFundModalOpen({
              isVisible: false,
              initialValues: {}
            })
          }
          initialValues={isManualFundModalOpen.initialValues}
        />
      </Modal>
      <AdvancedFilters
        open={isAdvancedFiltersOpen}
        addInvestments={(data: any) => {
          saveFundInvestments(data);
          setIsAdvancedFiltersOpen(false);
        }}
        close={() => setIsAdvancedFiltersOpen(false)}
        type={advancedFilterType}
      />
    </>
  );
};

export default compose(
  connect(
    (state: any) => ({
      investments:
        selectors.investmentAnalyserSelectors.researchInvestments(state),
      modelPortfolios: selectors.investmentAnalyserSelectors.cipOptions(state),
      advisor: selectors.advisorSelectors.getAdvisor(state)
    }),
    {
      updateAssetClasses: actions.metaActions.updateAssetClasses,
      updateResearchInvestments:
        actions.investmentAnalyserActions.updateResearchInvestments
    }
  )
)(InvestmentFilter);
